/**
 * useConsultationActions — acciones de calendario (confirmar/avanzar estado,
 * cancelar, reagendar) sobre una consulta puntual, vía las RPCs
 * dueño-o-director de sql/migrations/therapist_director_calendar_actions_2026_07_28.sql.
 *
 * Compartido entre AppointmentModal (TherapistCalendar, portal terapeuta) y
 * ClinicAppointmentModal (ClinicCalendar, portal director) — ambos llaman las
 * MISMAS RPCs; la autorización (dueño o director) se resuelve 100%
 * server-side, no hace falta lógica de permisos distinta en cada modal.
 *
 * Nunca expone el error real de Supabase al usuario (mensajes genéricos) —
 * el detalle sin PII se loguea en consola para debugging.
 */
import { useState } from 'react';
import { supabase } from '../lib/supabase/client';

interface UseConsultationActionsResult {
  actionLoading: boolean;
  actionError: string | null;
  clearActionError: () => void;
  advanceStatus: (consultationId: string) => Promise<string | null>;
  cancelConsultation: (consultationId: string) => Promise<boolean>;
  rescheduleConsultation: (consultationId: string, newFechaHoraISO: string) => Promise<boolean>;
  deleteConsultation: (consultationId: string) => Promise<boolean>;
}

export function useConsultationActions(): UseConsultationActionsResult {
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const clearActionError = () => setActionError(null);

  const advanceStatus = async (consultationId: string): Promise<string | null> => {
    setActionLoading(true);
    setActionError(null);
    try {
      const { data, error } = await supabase.rpc('advance_consultation_status_terapeuta', {
        p_consultation_id: consultationId,
      });
      if (error) throw error;
      return data as string;
    } catch (err) {
      console.error('[useConsultationActions] Error al avanzar estado:', err instanceof Error ? err.message : err);
      setActionError('No se pudo actualizar el estado. Intentá nuevamente.');
      return null;
    } finally {
      setActionLoading(false);
    }
  };

  const cancelConsultation = async (consultationId: string): Promise<boolean> => {
    setActionLoading(true);
    setActionError(null);
    try {
      const { error } = await supabase.rpc('cancel_consultation_terapeuta', {
        p_consultation_id: consultationId,
      });
      if (error) throw error;
      return true;
    } catch (err) {
      console.error('[useConsultationActions] Error al cancelar consulta:', err instanceof Error ? err.message : err);
      setActionError('No se pudo cancelar la cita. Intentá nuevamente.');
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const rescheduleConsultation = async (consultationId: string, newFechaHoraISO: string): Promise<boolean> => {
    setActionLoading(true);
    setActionError(null);
    try {
      const { error } = await supabase.rpc('reschedule_consultation_terapeuta', {
        p_consultation_id: consultationId,
        p_new_fecha_hora: newFechaHoraISO,
      });
      if (error) throw error;
      return true;
    } catch (err) {
      console.error('[useConsultationActions] Error al reagendar consulta:', err instanceof Error ? err.message : err);
      setActionError('No se pudo reagendar la cita. Verificá que el nuevo horario esté disponible.');
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const deleteConsultation = async (consultationId: string): Promise<boolean> => {
    setActionLoading(true);
    setActionError(null);
    try {
      const { error } = await supabase.rpc('delete_consultation', {
        p_consultation_id: consultationId,
      });
      if (error) throw error;
      return true;
    } catch (err) {
      console.error('[useConsultationActions] Error al eliminar consulta:', err instanceof Error ? err.message : err);
      setActionError('No se pudo eliminar la consulta. Intentá nuevamente.');
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  return {
    actionLoading,
    actionError,
    clearActionError,
    advanceStatus,
    cancelConsultation,
    rescheduleConsultation,
    deleteConsultation,
  };
}
